<template>
  <liste-template>
    <template #header>
      <h1 class="title is-size-3">{{ $t('listeCommandeAchat.title') }}</h1>
      <breadcrumb :breadcrumb="breadcrumb"/>
    </template>
    <template #action>
    </template>
    <template #bar-left>
      <form @submit.prevent="recherche()"
        class="column is-narrow  pdr-0">
        <b-input :placeholder="$t('general.button.search')"
          type="search"
          icon="magnify"
          v-model="critereRecherche.auto">
        </b-input>
      </form>
      <div class='column is-narrow'>
        <button class="button" @click='setShowFiltre'>
          <b-icon icon="filter-variant" size="is-small" type="is-default"></b-icon>
          <span class="is-size-7">
            {{$t('general.button.filtre')}}
            <label class="has-text-weight-bold"
              :class="nombreCritere > 0 ? '' : 'is-invisible'">
              ({{ nombreCritere }})
            </label>
          </span>
        </button>
        <span @click="reinitialiserFiltre()"
          class="info-circle-right is-pulled-right is-cursor-pointer">
          <b-icon icon="close-circle" type="is-danger"></b-icon>
        </span>
      </div>
    </template>
    <template #bar-right>
      <parametrage-table
        :reference-table="constantes.REFERENCE_TABLE_COMMANDE_ACHAT"
        @endLoadParamtre="startArrayRendering = true">
      </parametrage-table>
    </template>
    <template #content>
      <a ref="lienDownload"></a>
      <v-server-table
        url=""
        v-if="startArrayRendering"
        :columns="columns"
        :options="options"
        :ref="constantes.REFERENCE_TABLE_COMMANDE_ACHAT"
        @loaded="finLoading" @loading="debutLoading">
        <template slot='afterTable'>
          <table-items-in-view
            :perPage="resultPerPage"
            @input="$refs[constantes.REFERENCE_TABLE_COMMANDE_ACHAT].setLimit($event)"/>
        </template>
        <template #code = "{ row }">
          <router-link :to="{ name: 'c.baux.commande_achat-bailleur', params: { id: row.id } }"
            class="lien"
            :class="{'text-access-no-permission':
             !checkPermissions(constantes.COMMANDE_ACHAT.lecture)}">
            {{ row.code }}
          </router-link>
        </template>
        <template #bailleur = "{ row }">
          <router-link-blank v-if="row.intervenants.length > 0"
            :to="{ name: 'c.baux.bailleur',
              params: { id: row.intervenants[0].tiersId }}"
            :message="row.intervenants[0].nomCommercial">
          </router-link-blank>
        </template>
        <template #dateDevisCommande = "{ row }">
          {{ constantes.formatDate(row.dateDevisCommande)}}
        </template>
        <template #etat = "{ row }">
          {{ getLibelleEtat(row.etat) }}
        </template>
        <template #montantHt = "{ row }">
          <div class="is-pulled-right">
            {{ constantes.formatDevise(row.montantHT) }}
          </div>
        </template>
        <template #montantTtc = "{ row }">
          <div class="is-pulled-right">
            {{ constantes.formatDevise(row.montantTTC) }}
          </div>
        </template>
        <template #editer="{ row }">
          <btn-action-icon
            :tooltip-label="$t('general.button.edition')"
            :permission="constantes.COMMANDE_ACHAT.maj"
            @is-click="edition({format: pdf, typeDonnee: typeEditionCommande}, row.id)"
            icon="file-pdf-box">
          </btn-action-icon>
        </template>
        <template #modifier="{ row }">
          <btn-action-icon
            :tooltip-label="$t('general.button.edit')"
            :disabled="row.etat === sansSuite || row.etat === clos || row.interne"
            :permission="constantes.COMMANDE_ACHAT.editer"
            @is-click="$router.push({name: 'm.baux.commande_achat-bailleur',
              params: { id: row.id }})"
            icon-type="is-success"
            icon="pencil-box-outline">
          </btn-action-icon>
        </template>
      </v-server-table>
    </template>
    <template #after-content>
    </template>
    <template #filter>
      <div class="componentBrief" v-show="showFiltre">
        <filtre-commande-achat  ref="filtreListeCommandeAchat"
          @toggleFiltre="setShowFiltre"
          @recherche="makeRechercheFilter"/>
      </div>
    </template>
  </liste-template>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import listeTemplate from '@/components/general/template/listeTemplate';
import breadcrumb from '@/components/general/breadcrumb';
import { ORDRE_TRAVAIL_IMPRESSION } from '@/configuration/logistique/permissionsLogistiqueConf';
import { SET_DETECT_CHANGE_SOCIETE, SET_LOCK_MENU } from '@/store/modules/societe-mutation-type';
import subscribeMutation from '@/services/store/subscribeMutation';
import {
  TEXTS, SORT_ICON, NO_PER_PAGE_VALUE, RESULTS_PER_PAGE,
} from '@/configuration/clientTableConf';
import TableItemsInView from '@/components/general/TableItemsInView';
import calculNombreCritere from '@/services/general/calculNombreCritere';
import messageErreur from '@/services/general/messageErreur';
import filtreCommandeAchat from '@/components/baux/commandeAchat/filtreCommandeAchat';
import parametrageTable from '@/components/general/parametrageTable';
import { REFERENCE_TABLE_COMMANDE_ACHAT } from '@/configuration/general/referenceTableConf';
import tagInputToArray from '@/services/general/filters/tagInputToArray';
import formatIsoDate from '@/services/general/formatIsoDate';
import { COMMANDE_ACHAT } from '@/configuration/baux/permissionsBauxConf';
import {
  COMMANDE_ACHAT_BAILLEUR_ETAT,
  TYPE_EDITION_COMMANDE,
} from '@/configuration/commande/commandeConf';
import formatDate from '@/services/general/formatDate';
import { formatDevise } from '@/services/general/filters/formatDevise';
import routerLinkBlank from '@/components/general/routerLinkBlank';
import BtnActionIcon from '@/components/general/btnActionIcon';
import edition from '@/api/edition';
import { TYPE_PDF } from '@/configuration/edition/editionConf';
import { ETAT_SANSSUITE, ETAT_CLOTURER } from '@/configuration/devisConf';

const Permissions = createNamespacedHelpers('permissions');
const Societe = createNamespacedHelpers('societe');
const CommandeAchatBailleur = createNamespacedHelpers('commandeAchatBailleur');

export default {
  data() {
    return {
      permOtImpression: ORDRE_TRAVAIL_IMPRESSION,
      constantes: {
        REFERENCE_TABLE_COMMANDE_ACHAT,
        COMMANDE_ACHAT,
        formatDate,
        formatDevise,
      },
      unsubscribeValue: null,
      showFiltre: false,
      resultPerPage: RESULTS_PER_PAGE,
      startArrayRendering: false,
      pdf: TYPE_PDF,
      typeEditionCommande: TYPE_EDITION_COMMANDE,
      sansSuite: ETAT_SANSSUITE,
      clos: ETAT_CLOTURER,
    };
  },
  components: {
    BtnActionIcon,
    listeTemplate,
    breadcrumb,
    TableItemsInView,
    parametrageTable,
    filtreCommandeAchat,
    routerLinkBlank,
  },
  computed: {
    ...Permissions.mapGetters({
      checkPermissions: 'checkPermissions',
    }),
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
      lockMenu: 'lockMenu',
    }),
    ...CommandeAchatBailleur.mapGetters({
      commandes: 'items',
      totalCount: 'total',
      critereRecherche: 'criteresRecherche',
    }),
    breadcrumb() {
      return [
        { title: this.$t('general.breadcrumb.racine'), route: this.$PAGE_DEFAULT, isActive: false },
        { title: this.$t('listeCommandeAchat.title'), route: '', isActive: true },
      ];
    },
    nombreCritere() {
      const banCritereKey = ['auto', 'societeId', 'dates'];
      let nbCritere = calculNombreCritere(this.critereRecherche, banCritereKey);
      if (this.critereRecherche.dates
        && (this.critereRecherche.dates.dateDebut || this.critereRecherche.dates.dateFin)) {
        nbCritere += 1;
      }
      return nbCritere <= 9 ? nbCritere : '+9';
    },
    options() {
      return {
        sortIcon: SORT_ICON,
        headings: {
          code: this.$t('listeCommandeAchat.tab.code'),
          dateDevisCommande: this.$t('listeCommandeAchat.tab.date'),
          bailleur: this.$t('listeCommandeAchat.tab.bailleur'),
          etat: this.$t('listeCommandeAchat.tab.etat'),
          montantHt: this.$t('listeCommandeAchat.tab.montantHt'),
          montantTtc: this.$t('listeCommandeAchat.tab.montantTtc'),
          editer: '',
          modifier: '',
        },
        filterable: false,
        perPageValues: NO_PER_PAGE_VALUE,
        perPage: this.resultPerPage,
        texts: TEXTS,
        requestFunction: ((data) => {
          data.query = cloneDeep(this.critereRecherche);
          data.query.societeId = this.societeActiveId;
          if (data.query.tiersId.length) {
            data.query.tiersId = tagInputToArray(this.critereRecherche.tiersId, 'id');
          }
          data.query.dates.dateDebut = formatIsoDate(
            data.query.dates.dateDebut,
          );
          data.query.dates.dateFin = formatIsoDate(data.query.dates.dateFin);
          return this.actionLoadCommandes(data);
        }),
        responseAdapter: (() => {
          return {
            data: this.commandes,
            count: this.totalCount,
          };
        }),
        sortable: ['code', 'dateDevisCommande', 'etat', 'montantHt', 'montantTtc'],
        columnsClasses: {
          editer: 'colonneSelected',
          modifier: 'colonneSelected',
        },
      };
    },
    columns() {
      return ['code', 'bailleur', 'dateDevisCommande', 'etat', 'montantHt', 'montantTtc',
        'editer', 'modifier'];
    },
  },
  methods: {
    ...Permissions.mapActions({
      showPopUp: 'showPopup',
    }),
    ...Societe.mapMutations({
      setLockMenu: SET_LOCK_MENU,
    }),
    ...CommandeAchatBailleur.mapActions({
      actionLoadCommandes: 'loadCommandeAchat',
    }),
    finLoading() {
      this.loadingComponent.close();
    },
    debutLoading() {
      if (this.loadingComponent) this.loadingComponent.close();
      this.loadingComponent = this.$buefy.loading.open();
    },
    recherche() {
      this.$refs[this.constantes.REFERENCE_TABLE_COMMANDE_ACHAT].refresh();
    },
    makeRechercheFilter() {
      if (this.showFiltre) this.setShowFiltre();
      this.recherche();
    },
    goFirstPage() {
      this.$refs[this.constantes.REFERENCE_TABLE_COMMANDE_ACHAT].setPage(1);
    },
    getLibelleEtat(etatCmde) {
      const etatFind = COMMANDE_ACHAT_BAILLEUR_ETAT.find((etat) => {
        return etat.code === etatCmde;
      });
      return etatFind ? this.$i18n.t(etatFind.libelle) : '';
    },
    async setShowFiltre() {
      this.showFiltre = !this.showFiltre;
      if (this.showFiltre) await this.$refs.filtreListeCommandeAchat.initializeFiltre();
      this.setLockMenu(!this.lockMenu);
    },
    reinitialiserFiltre() {
      this.$refs.filtreListeCommandeAchat.initializeFiltre();
      this.$refs.filtreListeCommandeAchat.viderCritere();
      this.$refs.filtreListeCommandeAchat.appliquerCritere();
      this.showFiltre = false;
    },
    async edition(donnees, id) {
      const loadingComponent = this.$buefy.loading.open();
      try {
        const response = await edition({
          ids: [id],
          extension: donnees.format,
          typeDonnee: donnees.typeDonnee,
          typeEdition: donnees.typeEdition ? donnees.typeEdition : '',
        });
        const link = this.$refs.lienDownload;
        link.href = window.URL.createObjectURL(response.data);
        const disposition = response.request.getResponseHeader('Content-Disposition');
        link.download = decodeURI(disposition.match(/filename\*=UTF-8''(.*?)$/)[1]);
        link.click();
        loadingComponent.close();
      } catch (e) {
        messageErreur(this.$i18n);
        loadingComponent.close();
        throw (e);
      }
    },
  },
  beforeDestroy() {
    this.unsubscribeValue();
    if (this.lockMenu) this.setLockMenu(false);
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      this.unsubscribeValue = await subscribeMutation(this.$store, 'societe/' + SET_DETECT_CHANGE_SOCIETE, this.recherche, this.goFirstPage);
      this.setInformationPage(this.$t('pageBaux.commandeAchat.liste'));
      loadingComponent.close();
    } catch (e) {
      messageErreur(this.$i18n);
      loadingComponent.close();
      throw (e);
    }
  },
};
</script>

<style>

</style>
